/* Klecksy — pattern studio interactions for Horizon */
(() => {
  if (window.__klecksy) return;
  window.__klecksy = true;
  document.documentElement.classList.add('kx-js');
  const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  const finePointer = window.matchMedia('(hover: hover) and (pointer: fine)').matches;

  /* ---------- Scroll reveal ---------- */
  const revealObserver = 'IntersectionObserver' in window
    ? new IntersectionObserver((entries) => {
        entries.forEach((e) => {
          if (e.isIntersecting) {
            e.target.classList.add('is-in');
            revealObserver.unobserve(e.target);
          }
        });
      }, { rootMargin: '0px 0px -8% 0px', threshold: 0.08 })
    : null;

  function initReveal(root = document) {
    root.querySelectorAll('.kx-reveal:not(.is-in)').forEach((el) => {
      if (!revealObserver || reduceMotion) el.classList.add('is-in');
      else revealObserver.observe(el);
    });
  }

  /* ---------- Hero parallax ---------- */
  function initParallax(root = document) {
    if (reduceMotion || !finePointer) return;
    root.querySelectorAll('[data-kx-parallax]').forEach((hero) => {
      if (hero.dataset.kxBound) return;
      hero.dataset.kxBound = '1';
      const layers = hero.querySelectorAll('[data-depth]');
      let raf = 0;
      hero.addEventListener('pointermove', (ev) => {
        const r = hero.getBoundingClientRect();
        const x = (ev.clientX - r.left) / r.width - 0.5;
        const y = (ev.clientY - r.top) / r.height - 0.5;
        cancelAnimationFrame(raf);
        raf = requestAnimationFrame(() => {
          layers.forEach((l) => {
            const d = parseFloat(l.dataset.depth) || 0.5;
            l.style.translate = `${(-x * 28 * d).toFixed(1)}px ${(-y * 22 * d).toFixed(1)}px`;
          });
        });
      });
      hero.addEventListener('pointerleave', () => layers.forEach((l) => (l.style.translate = '0 0')));
    });
  }

  /* ---------- Wordmark: fit to width ---------- */
  function fitWordmarks(root = document) {
    root.querySelectorAll('.kx-wordmark__text').forEach((el) => {
      const box = el.parentElement;
      el.style.fontSize = '100px';
      const w = el.scrollWidth;
      if (!w) return;
      const target = box.clientWidth - 8;
      el.style.fontSize = `${Math.min(420, Math.floor((100 * target) / w))}px`;
    });
  }

  /* ---------- Pattern worlds accordion ---------- */
  class KxWorlds extends HTMLElement {
    connectedCallback() {
      this.items = [...this.querySelectorAll('[data-kx-world]')];
      this.items.forEach((item) => {
        const activate = () => this.activate(item);
        item.addEventListener('pointerenter', activate);
        item.addEventListener('focusin', activate);
      });
    }
    activate(item) {
      this.items.forEach((i) => i.classList.toggle('is-active', i === item));
    }
  }
  if (!customElements.get('kx-worlds')) customElements.define('kx-worlds', KxWorlds);

  /* ---------- Swatch wall tabs ---------- */
  class KxTabs extends HTMLElement {
    connectedCallback() {
      this.tabs = [...this.querySelectorAll('[role="tab"]')];
      this.tabs.forEach((tab, i) => {
        tab.addEventListener('click', () => this.select(i));
        tab.addEventListener('keydown', (e) => {
          const dir = { ArrowRight: 1, ArrowLeft: -1 }[e.key];
          if (!dir) return;
          e.preventDefault();
          const next = (i + dir + this.tabs.length) % this.tabs.length;
          this.select(next);
          this.tabs[next].focus();
        });
      });
    }
    select(index) {
      this.tabs.forEach((tab, i) => {
        const on = i === index;
        tab.setAttribute('aria-selected', String(on));
        tab.tabIndex = on ? 0 : -1;
        const panel = this.querySelector(`#${tab.getAttribute('aria-controls')}`);
        if (panel) panel.hidden = !on;
      });
    }
  }
  if (!customElements.get('kx-tabs')) customElements.define('kx-tabs', KxTabs);

  /* ---------- Counters ---------- */
  class KxCounter extends HTMLElement {
    connectedCallback() {
      const to = parseInt(this.dataset.to, 10);
      if (isNaN(to) || reduceMotion || !('IntersectionObserver' in window)) return;
      this.textContent = '0';
      const io = new IntersectionObserver(([e]) => {
        if (!e.isIntersecting) return;
        io.disconnect();
        const start = performance.now();
        const dur = 1400;
        const tick = (now) => {
          const p = Math.min(1, (now - start) / dur);
          const eased = 1 - Math.pow(1 - p, 4);
          this.textContent = String(Math.round(to * eased));
          if (p < 1) requestAnimationFrame(tick);
        };
        requestAnimationFrame(tick);
      }, { threshold: 0.6 });
      io.observe(this);
    }
  }
  if (!customElements.get('kx-counter')) customElements.define('kx-counter', KxCounter);

  /* ---------- Pattern loupe ---------- */
  class KxLoupe extends HTMLElement {
    connectedCallback() {
      this.frame = this.querySelector('.kx-loupe__frame');
      this.img = this.querySelector('.kx-loupe__img');
      this.lens = this.querySelector('.kx-loupe__lens');
      if (!this.frame || !this.img || !this.lens) return;
      this.zoom = parseFloat(this.dataset.zoom) || 2.5;
      this.setZoomSrc(this.frame.dataset.zoomSrc);

      const move = (ev) => this.move(ev);
      this.frame.addEventListener('pointerenter', (ev) => { this.frame.classList.add('is-zooming'); move(ev); });
      this.frame.addEventListener('pointermove', move);
      this.frame.addEventListener('pointerleave', () => this.frame.classList.remove('is-zooming'));
      this.frame.addEventListener('pointerdown', (ev) => {
        if (ev.pointerType !== 'mouse') { this.frame.classList.add('is-zooming'); move(ev); }
      });
      this.frame.addEventListener('pointerup', (ev) => {
        if (ev.pointerType !== 'mouse') this.frame.classList.remove('is-zooming');
      });
      this.frame.addEventListener('keydown', (e) => this.keyMove(e));
      this.frame.addEventListener('blur', () => this.frame.classList.remove('is-zooming'));

      this.querySelectorAll('.kx-loupe__thumb').forEach((btn) => {
        btn.addEventListener('click', () => {
          this.querySelectorAll('.kx-loupe__thumb').forEach((b) => b.setAttribute('aria-pressed', String(b === btn)));
          this.img.removeAttribute('srcset');
          this.img.src = btn.dataset.src;
          this.setZoomSrc(btn.dataset.zoomSrc);
        });
      });
    }
    setZoomSrc(src) {
      if (!src) return;
      this.lens.style.backgroundImage = `url("${src}")`;
      const pre = new Image();
      pre.src = src;
    }
    place(px, py) {
      const r = this.frame.getBoundingClientRect();
      const lw = this.lens.offsetWidth;
      const x = Math.max(0, Math.min(r.width, px));
      const y = Math.max(0, Math.min(r.height, py));
      this.pos = { x, y };
      this.lens.style.transform = `translate3d(${x - lw / 2}px, ${y - lw / 2}px, 0)`;
      const bw = r.width * this.zoom;
      const bh = r.height * this.zoom;
      this.lens.style.backgroundSize = `${bw}px ${bh}px`;
      this.lens.style.backgroundPosition = `${-(x * this.zoom - lw / 2)}px ${-(y * this.zoom - lw / 2)}px`;
    }
    move(ev) {
      const r = this.frame.getBoundingClientRect();
      this.place(ev.clientX - r.left, ev.clientY - r.top);
    }
    keyMove(e) {
      const step = 24;
      const d = { ArrowLeft: [-step, 0], ArrowRight: [step, 0], ArrowUp: [0, -step], ArrowDown: [0, step] }[e.key];
      if (!d) return;
      e.preventDefault();
      const r = this.frame.getBoundingClientRect();
      const p = this.pos || { x: r.width / 2, y: r.height / 2 };
      this.frame.classList.add('is-zooming');
      this.place(p.x + d[0], p.y + d[1]);
    }
  }
  if (!customElements.get('kx-loupe')) customElements.define('kx-loupe', KxLoupe);

  /* ---------- Size guide: dialogs and inch/cm toggle ---------- */
  document.addEventListener('click', (e) => {
    const opener = e.target.closest && e.target.closest('[data-kx-open]');
    if (opener) {
      const dlg = document.getElementById(opener.dataset.kxOpen);
      if (dlg && typeof dlg.showModal === 'function') {
        e.preventDefault();
        dlg.showModal();
      }
      return;
    }
    const closer = e.target.closest && e.target.closest('[data-kx-close]');
    if (closer) {
      closer.closest('dialog')?.close();
      return;
    }
    if (e.target.matches && e.target.matches('dialog.kx-dialog')) {
      const r = e.target.getBoundingClientRect();
      const inside = e.clientX >= r.left && e.clientX <= r.right && e.clientY >= r.top && e.clientY <= r.bottom;
      if (!inside) e.target.close();
      return;
    }
    const unitBtn = e.target.closest && e.target.closest('[data-kx-units] [data-unit]');
    if (unitBtn) {
      const unit = unitBtn.dataset.unit;
      const scope = unitBtn.closest('dialog, section, .kx-sizeblock') || document;
      scope.querySelectorAll('[data-kx-units] [data-unit]').forEach((b) => b.setAttribute('aria-pressed', String(b.dataset.unit === unit)));
      scope.querySelectorAll('[data-kx-chart] td[data-in]').forEach((td) => { td.textContent = td.dataset[unit]; });
      scope.querySelectorAll('[data-kx-chart] [data-unit-label]').forEach((l) => { l.textContent = unit; });
    }
  });

  /* ---------- Boot + theme editor support ---------- */
  function boot(root = document) {
    initReveal(root);
    initParallax(root);
    fitWordmarks(root);
  }

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', () => boot());
  else boot();

  if (document.fonts && document.fonts.ready) document.fonts.ready.then(() => fitWordmarks());
  let resizeT;
  window.addEventListener('resize', () => {
    clearTimeout(resizeT);
    resizeT = setTimeout(() => fitWordmarks(), 150);
  });

  document.addEventListener('shopify:section:load', (e) => boot(e.target));
  document.addEventListener('shopify:block:select', (e) => {
    const world = e.target.closest && e.target.closest('[data-kx-world]');
    if (world) world.closest('kx-worlds')?.activate(world);
    const tab = e.target.closest && e.target.closest('[role="tab"]');
    if (tab) {
      const tabs = tab.closest('kx-tabs');
      tabs?.select(tabs.tabs.indexOf(tab));
    }
  });
})();
